import { useMemo } from "react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import type { BucketSize, PeriodRunsRow, WorkflowStats } from "@/lib/analytics/types";
import { bucketWord } from "@/lib/analytics/buckets";

const COLORS = [
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--chart-5)",
];

// Suffix used for compare-period dataKey so it doesn't collide with the
// workflow-name dataKey of period A. Kept out-of-band (private).
const COMPARE_SUFFIX = "__cmp";

interface RunsOverTimeProps {
  rows: PeriodRunsRow[];
  workflows: WorkflowStats[];
  bucket: BucketSize;
  /** Period B rows (shifted to align with period A labels). */
  compareRows?: PeriodRunsRow[];
}

interface CustomTooltipProps {
  active?: boolean;
  label?: string | number;
  payload?: ReadonlyArray<{ payload?: Record<string, unknown> }>;
  top: WorkflowStats[];
  hasCompare: boolean;
}

function CustomTooltip({ active, label, payload, top, hasCompare }: CustomTooltipProps) {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0]?.payload;
  if (!row) return null;

  const comparePeriod = hasCompare ? String(row["_comparePeriod"] ?? "") : "";

  let currentTotal = 0;
  let compareTotal = 0;
  const rows: Array<{ name: string; color: string; current: number; compare: number }> = [];
  for (let i = 0; i < top.length; i++) {
    const wf = top[i];
    if (!wf) continue;
    const current = Number(row[wf.name] ?? 0);
    const compare = hasCompare ? Number(row[`${wf.name}${COMPARE_SUFFIX}`] ?? 0) : 0;
    currentTotal += current;
    compareTotal += compare;
    // Skip rows that are zero in both — reduces noise in empty buckets.
    if (current === 0 && compare === 0) continue;
    rows.push({
      name: wf.name,
      color: COLORS[i % COLORS.length] as string,
      current,
      compare,
    });
  }

  return (
    <div
      className="rounded-md border bg-popover px-3 py-2 text-xs shadow-sm"
      style={{
        background: "var(--popover)",
        border: "1px solid var(--border)",
      }}
    >
      <div className="flex items-baseline justify-between gap-4 pb-1.5 mb-1.5 border-b border-border">
        <div className="font-medium">{String(label ?? "")}</div>
        {hasCompare ? (
          <div className="text-muted-foreground">vs {comparePeriod}</div>
        ) : null}
      </div>

      {rows.length === 0 ? (
        <div className="text-muted-foreground">No runs</div>
      ) : (
        <div className="space-y-1">
          {rows.map((r) => (
            <div key={r.name} className="flex items-center gap-2">
              <span
                aria-hidden
                className="inline-block size-2 rounded-sm shrink-0"
                style={{ background: r.color }}
              />
              <span className="flex-1 truncate max-w-[18rem]">{r.name}</span>
              <span className="tabular-nums font-medium">{r.current}</span>
              {hasCompare ? (
                <span className="tabular-nums text-muted-foreground w-8 text-right">
                  {r.compare}
                </span>
              ) : null}
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 pt-1.5 mt-1.5 border-t border-border">
        <span className="flex-1 font-medium">Total</span>
        <span className="tabular-nums font-semibold">{currentTotal}</span>
        {hasCompare ? (
          <span className="tabular-nums text-muted-foreground w-8 text-right">{compareTotal}</span>
        ) : null}
      </div>
    </div>
  );
}

export function RunsOverTime({ rows, workflows, bucket, compareRows }: RunsOverTimeProps) {
  const top = useMemo(() => workflows.slice(0, 5), [workflows]);
  const word = bucketWord(bucket);
  const hasCompare = compareRows != null && compareRows.length > 0;

  // Merge current + compare rows aligned by index. Compare counts use a
  // <workflowName>__cmp dataKey so both can stack in parallel.
  const data = useMemo(() => {
    return rows.map((row, i) => {
      const entry: Record<string, number | string> = { period: row.period };
      for (const wf of top) {
        entry[wf.name] = row.perWorkflow[wf.workflowId] ?? 0;
      }
      if (compareRows) {
        const cmp = compareRows[i];
        for (const wf of top) {
          entry[`${wf.name}${COMPARE_SUFFIX}`] = cmp?.perWorkflow[wf.workflowId] ?? 0;
        }
        entry["_comparePeriod"] = cmp?.period ?? "";
      }
      return entry;
    });
  }, [rows, top, compareRows]);

  // For dense day-bucket datasets (>=90 points), space out X-axis ticks.
  const tickInterval = useMemo(() => {
    if (bucket !== "day" || data.length < 90) return undefined;
    return Math.max(1, Math.floor(data.length / 15)) - 1;
  }, [bucket, data.length]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Runs per {word}</CardTitle>
        <CardDescription>
          {hasCompare
            ? `Top 5 workflows — solid = current, translucent = previous period`
            : `Top 5 workflows, stacked by ${word}`}
        </CardDescription>
      </CardHeader>
      <CardContent className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 12, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis
              dataKey="period"
              stroke="var(--muted-foreground)"
              fontSize={12}
              interval={tickInterval}
              minTickGap={40}
            />
            <YAxis stroke="var(--muted-foreground)" fontSize={12} />
            <Tooltip
              cursor={{ fill: "var(--muted)", fillOpacity: 0.15 }}
              content={(props) => (
                <CustomTooltip
                  active={props.active}
                  label={props.label as string | number | undefined}
                  payload={props.payload as unknown as ReadonlyArray<{ payload?: Record<string, unknown> }> | undefined}
                  top={top}
                  hasCompare={hasCompare}
                />
              )}
            />
            <Legend wrapperStyle={{ fontSize: "0.75rem" }} />
            {top.map((wf, i) => (
              <Bar
                key={wf.workflowId}
                dataKey={wf.name}
                stackId="current"
                fill={COLORS[i % COLORS.length]}
                radius={[2, 2, 0, 0]}
              />
            ))}
            {hasCompare
              ? top.map((wf, i) => (
                  <Bar
                    key={`${wf.workflowId}-cmp`}
                    dataKey={`${wf.name}${COMPARE_SUFFIX}`}
                    stackId="previous"
                    fill={COLORS[i % COLORS.length]}
                    fillOpacity={0.35}
                    radius={[2, 2, 0, 0]}
                    legendType="none"
                  />
                ))
              : null}
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
